import { useId, useState, type FormEvent } from "react";
import { GitBranch } from "lucide-react";
import {
  GITHUB_ALLOWED_OWNERS_DEFAULT,
  isGitBranchName,
  parseGitHubRepoUrl,
  type GroomingRepoHost,
} from "@ttc-spectree/toolbox-contract";
import { Button } from "@/components/ui/button";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * The Admin's "Add a GitHub repository" form on Project → Repositories (epic
 * PI-SPEC-SW-E318, feature PI-SPEC-SW-1679; ER-1683 Resolved Decision 2).
 *
 * The Admin pastes the repository's web address
 * (`https://github.com/TTC-SpecTree/SpecTree`); the form reads owner and
 * repository with the contract's `parseGitHubRepoUrl` as they type and shows
 * them ("TTC-SpecTree / SpecTree"). Branch is optional: blank means the
 * repository's default branch, and a `/tree/<branch>` address fills it in.
 * SpecTree never calls GitHub, so a typo in the address is caught only when a
 * computer first copies the repository (Setup step 5 names it).
 *
 * Inline, plain errors stop the save: an address that is not a github.com
 * repository, an owner outside the server's allowed list
 * (`config.allowedGitHubOwners`, default {@link GITHUB_ALLOWED_OWNERS_DEFAULT}),
 * and a name the project already maps on either host (ER-1683 Resolved
 * Decision 4: repository names are unique per ADO project across hosts).
 * When the save itself fails, the page passes the server's 400 message, shown
 * word for word.
 *
 * Saving hands the page `{ host: "github", owner, repo, branch? }`; the page
 * PUTs the config without `enabled`, as the ADO row does.
 */

/** The address the placeholder shows (the pilot repository). */
export const GITHUB_REPO_URL_PLACEHOLDER = "https://github.com/TTC-SpecTree/SpecTree";

/** A pasted address that is not a github.com repository. */
export const GITHUB_URL_INVALID =
  "That isn't a GitHub repository address. Paste one like https://github.com/TTC-SpecTree/SpecTree.";

/** A branch name Git would refuse. */
export const GITHUB_BRANCH_INVALID = "That isn't a branch name Git accepts. Leave it blank to use the default branch.";

/** What the form saves: one GitHub repository. */
export interface GitHubRepoFormValue {
  host: "github";
  owner: string;
  repo: string;
  branch?: string;
}

/** A mapped repository as the form compares names (the config read's entries). */
export interface MappedRepoName {
  project?: string | undefined;
  repo: string;
  host?: GroomingRepoHost | undefined;
}

/**
 * "SpecTree can read GitHub repositories from TTC-SpecTree, TTC-GGI and
 * TTC-POC only.", built from the allowed list (the same sentence the API
 * refuses with). Pure; exported for tests.
 */
export function allowedOwnersText(owners: readonly string[]): string {
  const names =
    owners.length <= 1 ? (owners[0] ?? "") : `${owners.slice(0, -1).join(", ")} and ${owners[owners.length - 1] ?? ""}`;
  return `SpecTree can read GitHub repositories from ${names} only.`;
}

/** "This project already has a repository named SpecTree." Pure; exported for tests. */
export function duplicateRepoText(repo: string): string {
  return `This project already has a repository named ${repo}.`;
}

/**
 * True when `project` already maps a repository named `repo` on either host
 * (case-insensitive). A GitHub entry belongs to the configured project; an
 * ADO entry to its own `project`, else the configured one. Mirrors the API's
 * duplicate rule. Pure; exported for tests.
 */
export function isRepoNameMapped(repos: readonly MappedRepoName[], project: string, repo: string): boolean {
  const projectKey = project.toLowerCase();
  const repoKey = repo.toLowerCase();
  return repos.some((entry) => {
    const owning = entry.host === "github" ? project : (entry.project ?? project);
    return owning.toLowerCase() === projectKey && entry.repo.toLowerCase() === repoKey;
  });
}

/** What the form reads from its two inputs: the value to save, or the one plain problem. */
export type GitHubRepoFormCheck =
  | { kind: "empty" }
  | { kind: "problem"; field: "url" | "branch"; text: string; owner?: string; repo?: string }
  | { kind: "ok"; value: GitHubRepoFormValue };

/**
 * Check the pasted address and branch against the parser, the allowed owners
 * and the mapped names. The owner is saved in the allowed list's casing.
 * Pure; exported for tests.
 */
export function checkGitHubRepoForm(input: {
  url: string;
  branch: string;
  project: string;
  repos: readonly MappedRepoName[];
  allowedOwners: readonly string[];
}): GitHubRepoFormCheck {
  if (input.url.trim().length === 0) return { kind: "empty" };
  const parsed = parseGitHubRepoUrl(input.url);
  if (!parsed) return { kind: "problem", field: "url", text: GITHUB_URL_INVALID };
  const owner = input.allowedOwners.find((name) => name.toLowerCase() === parsed.owner.toLowerCase());
  if (owner === undefined) {
    return { kind: "problem", field: "url", text: allowedOwnersText(input.allowedOwners), owner: parsed.owner, repo: parsed.repo };
  }
  if (isRepoNameMapped(input.repos, input.project, parsed.repo)) {
    return { kind: "problem", field: "url", text: duplicateRepoText(parsed.repo), owner, repo: parsed.repo };
  }
  const branch = input.branch.trim();
  if (branch.length > 0 && !isGitBranchName(branch)) {
    return { kind: "problem", field: "branch", text: GITHUB_BRANCH_INVALID, owner, repo: parsed.repo };
  }
  const value: GitHubRepoFormValue = { host: "github", owner, repo: parsed.repo };
  if (branch.length > 0) value.branch = branch;
  return { kind: "ok", value };
}

const INPUT_BOX =
  "h-8 w-full rounded-lg border border-[#3F3F46] bg-[#111113] px-2.5 text-[13px] text-foreground outline-none placeholder:text-muted-foreground focus:border-link";

export interface GitHubRepoFormProps {
  project: string;
  /** Every repository the project maps now, on either host. */
  repos: readonly MappedRepoName[];
  /** The server's allowed GitHub owners (`config.allowedGitHubOwners`); the contract default when absent. */
  allowedOwners?: readonly string[] | undefined;
  pending: boolean;
  /** Why the last save failed, word for word from the server when it said; null when it did not fail. */
  saveError: string | null;
  onSave: (repo: GitHubRepoFormValue) => void;
  onCancel: () => void;
}

/** The new GitHub row in the Admin's table: paste the address, optional branch, Save and Cancel. */
export function GitHubRepoForm({
  project,
  repos,
  allowedOwners,
  pending,
  saveError,
  onSave,
  onCancel,
}: GitHubRepoFormProps) {
  const urlId = useId();
  const branchId = useId();
  const [url, setUrl] = useState("");
  const [branch, setBranch] = useState("");
  const [branchTouched, setBranchTouched] = useState(false);
  const owners = allowedOwners && allowedOwners.length > 0 ? allowedOwners : GITHUB_ALLOWED_OWNERS_DEFAULT;
  const check = checkGitHubRepoForm({ url, branch, project, repos, allowedOwners: owners });
  const parsed = url.trim().length > 0 ? parseGitHubRepoUrl(url) : null;

  const onUrlChange = (next: string) => {
    setUrl(next);
    // A `/tree/<branch>` address fills the branch until the Admin types one.
    if (!branchTouched) setBranch(parseGitHubRepoUrl(next)?.branch ?? "");
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (check.kind !== "ok" || pending) return;
    onSave(check.value);
  };

  return (
    <li className="border-b border-[#141417] px-[18px] py-3 last:border-b-0" data-testid="github-repo-form">
      <form className="flex flex-col gap-2.5" onSubmit={submit} noValidate>
        <div className="flex flex-col gap-1">
          <label htmlFor={urlId} className={cn(TOOLBOX_TEXT.meta, "text-muted-foreground")}>
            GitHub repository URL
          </label>
          <input
            id={urlId}
            type="text"
            inputMode="url"
            autoComplete="off"
            spellCheck={false}
            className={cn(INPUT_BOX, "font-mono text-xs")}
            placeholder={GITHUB_REPO_URL_PLACEHOLDER}
            value={url}
            onChange={(event) => {
              onUrlChange(event.target.value);
            }}
            aria-invalid={check.kind === "problem" && check.field === "url"}
            data-testid="github-repo-url"
          />
          {parsed ? (
            <span
              className={cn("flex items-center gap-2 font-mono text-xs", TOOLBOX_TEXT.faint)}
              data-testid="github-repo-preview"
            >
              <GitBranch className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {parsed.owner} / {parsed.repo}
            </span>
          ) : null}
        </div>
        <div className="flex max-w-[320px] flex-col gap-1">
          <label htmlFor={branchId} className={cn(TOOLBOX_TEXT.meta, "text-muted-foreground")}>
            Branch (optional)
          </label>
          <input
            id={branchId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            className={cn(INPUT_BOX, "font-mono text-xs")}
            placeholder="default branch"
            value={branch}
            onChange={(event) => {
              setBranch(event.target.value);
              setBranchTouched(true);
            }}
            aria-invalid={check.kind === "problem" && check.field === "branch"}
            data-testid="github-repo-branch"
          />
          <span className={cn(TOOLBOX_TEXT.meta, TOOLBOX_TEXT.faint)}>
            Leave it blank to use the repository&apos;s default branch.
          </span>
        </div>
        {check.kind === "problem" ? (
          <p className={cn(TOOLBOX_TEXT.content, "text-destructive")} role="alert" data-testid="github-repo-error">
            {check.text}
          </p>
        ) : saveError ? (
          <p className={cn(TOOLBOX_TEXT.content, "text-destructive")} role="alert" data-testid="github-repo-save-error">
            {saveError}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className={cn(TOOLBOX_TEXT.meta, TOOLBOX_TEXT.faint)}>
            SpecTree doesn&apos;t contact GitHub. Each computer copies the repository at its next update, signed in to
            GitHub through Setup.
          </span>
          <span className="flex justify-end gap-1">
            <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              className="border-primary bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
              disabled={check.kind !== "ok" || pending}
            >
              {pending ? "Saving…" : "Save"}
            </Button>
          </span>
        </div>
      </form>
    </li>
  );
}
